/**
 * Товары: что показывает таблица и чем она фильтруется.
 *
 * Фильтры выполняет сервер — он же знает остатки по всей сети и умеет
 * считать «без фото» и «заканчивается». Поэтому здесь не фильтрация, а
 * перевод состояния экрана в параметры запроса.
 *
 * Номенклатуру, цены и остатки ведёт FOM, админка их только читает (R-15).
 */
import { formatAmount, withCountRu } from '@dorixona/utils'
import type { Product, ProductQuery, Stock } from './api'
import type { Store } from '../stores/api'

/** «38 000 сум»; цены нет — прочерк, это товар, который ещё не продаётся. */
export const priceLabel = (product: Product): string =>
  product.price ? `${formatAmount(product.price)} сум` : '—'

/** «1 240 шт.» — остаток по сети; ноль показывает таблица отдельной меткой. */
export const stockLabel = (product: Product): string => `${formatAmount(product.stockQty)} шт.`

export type ProductType = 'all' | 'rx' | 'otc'
export type ProductState = 'all' | 'on' | 'off' | 'zero' | 'nophoto' | 'low'

export type SortKey = 'name' | 'price_asc' | 'price_desc' | 'popular'

/** Сколько строк на странице можно выбрать; больше 100 сервер не отдаёт. */
export const PAGE_SIZES = [10, 25, 50, 100] as const
export type PageSize = (typeof PAGE_SIZES)[number]

export interface ProductFilters {
  q: string
  /** 0 — все категории, -1 — товары без категории. */
  cat: number
  type: ProductType
  state: ProductState
  sort: SortKey
  /** Страница с 1; листает сервер. */
  page: number
  size: PageSize
}

export const EMPTY_FILTERS: ProductFilters = {
  q: '',
  cat: 0,
  type: 'all',
  state: 'all',
  sort: 'name',
  page: 1,
  size: 10,
}

const TYPES: ProductType[] = ['all', 'rx', 'otc']
const STATES: ProductState[] = ['all', 'on', 'off', 'zero', 'nophoto', 'low']
const SORTS: SortKey[] = ['name', 'price_asc', 'price_desc', 'popular']

function oneOf<T extends string | number>(value: T, allowed: readonly T[], fallback: T): T {
  return allowed.includes(value) ? value : fallback
}

function positive(raw: string | null, fallback: number): number {
  const n = Number(raw)
  return Number.isInteger(n) && n > 0 ? n : fallback
}

/**
 * Состояние экрана живёт в адресе: ссылкой на отобранный список можно
 * поделиться, а «Назад» из карточки товара возвращает на ту же страницу.
 * Мусор в адресе не ломает экран — неизвестное значение становится значением
 * по умолчанию.
 */
export function filtersFromParams(params: URLSearchParams): ProductFilters {
  return {
    q: params.get('q') ?? '',
    cat: Number.isInteger(Number(params.get('cat'))) ? Number(params.get('cat')) : 0,
    type: oneOf(params.get('type') as ProductType, TYPES, EMPTY_FILTERS.type),
    state: oneOf(params.get('state') as ProductState, STATES, EMPTY_FILTERS.state),
    sort: oneOf(params.get('sort') as SortKey, SORTS, EMPTY_FILTERS.sort),
    page: positive(params.get('page'), 1),
    size: oneOf(
      positive(params.get('size'), EMPTY_FILTERS.size) as PageSize,
      PAGE_SIZES,
      EMPTY_FILTERS.size,
    ),
  }
}

/** Обратно в адрес — только то, что отличается от значения по умолчанию. */
export function filtersToParams(filters: ProductFilters): URLSearchParams {
  const params = new URLSearchParams()
  for (const key of Object.keys(EMPTY_FILTERS) as (keyof ProductFilters)[]) {
    if (filters[key] !== EMPTY_FILTERS[key]) params.set(key, String(filters[key]))
  }
  return params
}

/** Сужен ли список: сортировка, страница и размер страницы — не фильтры. */
export const isFiltered = (filters: ProductFilters): boolean =>
  filters.q !== EMPTY_FILTERS.q ||
  filters.cat !== EMPTY_FILTERS.cat ||
  filters.type !== EMPTY_FILTERS.type ||
  filters.state !== EMPTY_FILTERS.state

/**
 * Состояние экрана → параметры `GET /admin/products`.
 *
 * `lowStock` берётся из настройки `catalog.low_stock_threshold`: порог
 * «заканчивается» задаёт бизнес, а не экран.
 */
export function toQuery(filters: ProductFilters, lowStock: number): ProductQuery {
  return {
    q: filters.q.trim(),
    categoryId: filters.cat > 0 ? filters.cat : undefined,
    uncategorized: filters.cat === -1 ? true : undefined,
    rx: filters.type === 'all' ? '' : filters.type,
    active: filters.state === 'on' ? true : filters.state === 'off' ? false : undefined,
    inStock: filters.state === 'zero' ? false : undefined,
    noPhoto: filters.state === 'nophoto' ? true : undefined,
    lowStock: filters.state === 'low' ? lowStock : undefined,
    sort: filters.sort,
    page: filters.page,
    pageSize: filters.size,
  }
}

/** Процент кэшбэка товара — свой либо категорийный; считает сервер (R-12). */
export const cashbackLabel = (product: Product): string =>
  product.cbOwn === null ? `${product.cb}% (категория)` : `${product.cb}%`

/** Больше 5 МБ сервер не примет — проверяем до выгрузки, а не после. */
export const PHOTO_MAX_BYTES = 5 * 1024 * 1024
export const PHOTO_TYPES = ['image/jpeg', 'image/png', 'image/webp']

/**
 * Какие файлы можно отправить и что сказать про остальные. Всё проверяем
 * до выгрузки: ждать отказа сервера после долгой отправки незачем.
 */
export function sortPhotos(files: File[]): { good: File[]; error: string } {
  const good = files.filter((f) => PHOTO_TYPES.includes(f.type) && f.size <= PHOTO_MAX_BYTES)
  const wrongType = files.filter((f) => !PHOTO_TYPES.includes(f.type)).length
  const tooBig = files.length - good.length - wrongType
  const problems = [
    wrongType ? `${withCountRu(wrongType, ['файл', 'файла', 'файлов'])} не JPEG, PNG или WebP` : '',
    tooBig ? `${withCountRu(tooBig, ['файл', 'файла', 'файлов'])} больше 5 МБ` : '',
  ].filter(Boolean)
  return { good, error: problems.length ? `Не загружены: ${problems.join(', ')}` : '' }
}

/** Итог остатков по сети: сколько штук, в скольких аптеках и разброс цен. */
export interface StockSummary {
  qty: number
  stores: number
  /** Наименьшая и наибольшая цена там, где товар есть; 0 — нигде нет. */
  min: number
  max: number
}

export function stockSummary(rows: Stock[]): StockSummary {
  const present = rows.filter((r) => r.qty > 0)
  const prices = present.map((r) => r.price).filter((p) => p > 0)
  return {
    qty: rows.reduce((sum, r) => sum + r.qty, 0),
    stores: present.length,
    min: prices.length ? Math.min(...prices) : 0,
    max: prices.length ? Math.max(...prices) : 0,
  }
}

/**
 * Цена товара по сети: в аптеках она разная (F-44), поэтому, когда остатки
 * известны, — «52 000 – 68 000 сум», а до того — наименьшая цена из карточки.
 */
export function priceRangeLabel(product: Product, summary: StockSummary | null): string {
  if (!summary || summary.min === summary.max) return priceLabel(product)
  return `${formatAmount(summary.min)} – ${formatAmount(summary.max)} сум`
}

/** Строка вкладки «Остатки»: аптека сети и сколько в ней товара. */
export interface StockRow {
  storeId: number
  name: string
  /** Адрес аптеки; нет списка аптек — хотя бы район из остатков. */
  place: string
  qty: number
  /** Цена в этой аптеке; 0 — товара там нет и цены FOM не прислал. */
  price: number
  /** Аптека работает; выключенная не выдаёт заказы, даже если товар есть. */
  active: boolean
}

/**
 * Остатки по всей сети: каждая аптека — строкой, даже где товара нет, иначе
 * не видно, что в Бектемире он кончился. Без списка аптек (у роли нет доступа
 * к разделу «Аптеки») — только те, что прислал FOM.
 */
export function stockRows(stock: Stock[], stores: Store[] | undefined): StockRow[] {
  const byStore = new Map(stock.map((s) => [s.storeId, s]))
  const known = (stores ?? []).map<StockRow>((st) => {
    const row = byStore.get(st.id)
    return {
      storeId: st.id,
      name: st.name,
      place: st.address || st.district,
      qty: row ? row.qty : 0,
      price: row ? row.price : 0,
      active: st.active,
    }
  })
  const listed = new Set(known.map((r) => r.storeId))
  const rest = stock
    .filter((s) => !listed.has(s.storeId))
    .map<StockRow>((s) => ({
      storeId: s.storeId,
      name: s.storeName,
      place: s.district,
      qty: s.qty,
      price: s.price,
      active: true,
    }))
  // Сначала то, откуда можно забрать: больше остаток — выше.
  return [...known, ...rest].sort((a, b) => b.qty - a.qty)
}
